'use client';

import { useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { ArrowRight } from 'lucide-react';
import { useRouter } from 'next/navigation';

export default function NotFound() {
  const router = useRouter();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const { scrollYProgress } = useScroll();
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 360]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-background">
      {/* Bold geometric background element */}
      <motion.div
        className="absolute -right-1/4 top-0 h-[120vh] w-[60vw] bg-gradient-to-br from-orange-500/5 to-red-500/5 dark:from-orange-500/10 dark:to-red-500/10"
        style={{
          clipPath: 'polygon(30% 0%, 100% 0%, 100% 100%, 0% 100%)',
          x: mousePosition.x,
          y: mousePosition.y,
        }}
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
      />

      {/* Rotating accent circle */}
      <motion.div
        className="absolute left-[5%] top-[15%] h-32 w-32 rounded-full border-4 border-orange-500/20 dark:border-orange-500/30 md:h-48 md:w-48"
        style={{ rotate }}
      />

      {/* Main content container */}
      <div className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col justify-between px-6 py-12 md:px-12 lg:px-16">
        {/* Header navigation hint */}
        <motion.div
          className="flex items-center gap-2 text-sm font-medium text-muted-foreground"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <span>ERROR</span>
          <span className="text-orange-500">/</span>
          <span>404</span>
        </motion.div>

        {/* Main content area */}
        <div className="flex flex-col gap-8 md:gap-12">
          {/* Large 404 text - asymmetric placement */}
          <div className="relative -ml-2 md:-ml-4">
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              {/* Number with striking typography */}
              <h1
                className="font-playfair text-[12rem] font-black leading-[0.85] tracking-tighter text-foreground md:text-[18rem] lg:text-[24rem]"
              >
                4<span className="text-orange-500">0</span>4
              </h1>

              {/* Overlapping text element */}
              <motion.div
                className="absolute -right-4 bottom-0 rotate-90 text-sm font-bold uppercase tracking-[0.3em] text-orange-500/40 md:-right-8 md:text-base"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.8 }}
              >
                NOT FOUND
              </motion.div>
            </motion.div>

            {/* Decorative line */}
            <motion.div
              className="mt-6 h-1 w-32 bg-orange-500 md:w-48"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{
                delay: 0.4,
                duration: 0.8,
                ease: [0.22, 1, 0.36, 1],
              }}
              style={{ transformOrigin: 'left' }}
            />
          </div>

          {/* Content text - asymmetric layout */}
          <motion.div
            className="ml-0 max-w-2xl md:ml-8 lg:ml-16"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            <h2 className="mb-6 text-3xl font-light leading-tight text-foreground md:text-4xl lg:text-5xl">
              This page doesn't exist
            </h2>

            <motion.button
              onClick={() => router.push('/')}
              className="group relative overflow-hidden bg-foreground px-8 py-4 text-base font-semibold text-background transition-all hover:bg-foreground/90"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="relative z-10 flex items-center justify-center gap-3">
                Return Home
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </span>
              <motion.div
                className="absolute inset-0 bg-orange-500"
                initial={{ x: '-100%' }}
                whileHover={{ x: 0 }}
                transition={{ duration: 0.3 }}
              />
            </motion.button>
          </motion.div>
        </div>

        {/* Footer removed */}
        <div />
      </div>

      {/* Subtle accent elements */}
      <motion.div
        className="pointer-events-none absolute bottom-[10%] right-[10%] h-4 w-4 rounded-full bg-orange-500/30"
        animate={{
          scale: [1, 1.5, 1],
          opacity: [0.3, 0.6, 0.3],
        }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="pointer-events-none absolute left-[15%] top-[60%] h-3 w-3 rotate-45 bg-orange-500/20"
        animate={{
          rotate: [45, 225, 45],
        }}
        transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
      />
    </div>
  );
}
